import { useCreateAccount } from '@application/api';
import { SeoController } from '@application/components';
import { SUPPORTED_COUNTRIES, SUPPORTED_PHONE_NUMBERS } from '@application/constants/countries';
import { formatPhoneNumber, scrollToError, useRouter, useValidation } from '@application/utils';
import { CountryCode } from '@gql/graphql';
import type { JoinBreederPageFormValues } from '@presentation/pages';
import { JoinBreederPage } from '@presentation/pages';
import { FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import type { ObjectSchema } from 'yup';
import { boolean, mixed, object, ref, string } from 'yup';

export const JoinBreederPageController: FC = () => {
  const { routeLocationDescriptor, translate, countryCode } = useRouter();
  const {
    emailMessage,
    minCharactersMessage,
    passwordDifferentMessage,
    invalidMessage,
    isValidMobilePhone,
    requiredMessage,
    isValidEmail,
  } = useValidation();

  const { createAccount, createAccountLoading } = useCreateAccount();

  const initialValues: JoinBreederPageFormValues = useMemo(() => {
    const formattedCountry = SUPPORTED_COUNTRIES.includes(countryCode)
      ? countryCode
      : CountryCode.BE;
    return {
      firstName: '',
      lastName: '',
      email: '',
      phonePrefixCountry: formattedCountry.toUpperCase(),
      phoneNumber: '',
      country: formattedCountry.toUpperCase() as CountryCode,
      password: '',
      confirmPassword: '',
      acceptedChart: false,
      acceptedTerms: false,
    };
  }, [countryCode]);

  const validationSchema = useMemo(() => {
    const schema: ObjectSchema<JoinBreederPageFormValues> = object({
      firstName: string().required(requiredMessage),
      lastName: string().required(requiredMessage),
      email: string()
        .email(emailMessage)
        .required(requiredMessage)
        .test('is-valid-email', emailMessage, (value) => isValidEmail(value)),

      phonePrefixCountry: string().oneOf(SUPPORTED_PHONE_NUMBERS).required(requiredMessage),
      phoneNumber: string()
        .test('is-valid', invalidMessage, (value, context) => {
          if (value === null) {
            return true;
          }

          if (SUPPORTED_COUNTRIES.includes(context.parent.phonePrefixCountry)) {
            if (context.parent.phonePrefixCountry === 'DE') {
              return (
                value?.match(/^0?((\+?)([0-9]){10})$/) || value?.match(/^0?((\+?)([0-9]){11})$/)
              );
            }
            return value?.match(/^0?((\+?)([0-9]){9})$/);
          }

          return isValidMobilePhone(value as string);
        })
        .required(requiredMessage),
      country: mixed<CountryCode>().required(requiredMessage),
      password: string().min(6, minCharactersMessage(6)).required(requiredMessage),
      confirmPassword: string()
        .oneOf([ref('password'), null], passwordDifferentMessage)
        .required(requiredMessage),
      acceptedChart: boolean().isTrue(requiredMessage).required(requiredMessage),
      acceptedTerms: boolean().isTrue(requiredMessage).required(requiredMessage),
    }).defined();
    return schema;
  }, [
    emailMessage,
    invalidMessage,
    isValidMobilePhone,
    minCharactersMessage,
    passwordDifferentMessage,
    requiredMessage,
    isValidEmail,
  ]);

  const form = useFormik<JoinBreederPageFormValues>({
    validationSchema,
    initialValues,
    onSubmit: async (values) => {
      createAccount({
        breederFormValues: values,
        phone: formatPhoneNumber(values.phoneNumber, values.phonePrefixCountry),
      });
    },
    enableReinitialize: true,
  });
  const onSubmitClickClickHandler = useCallback(async () => {
    await form.submitForm();
    scrollToError(form.errors);
  }, [form]);

  const isLoading = useMemo(() => {
    return createAccountLoading || form.isSubmitting;
  }, [createAccountLoading, form.isSubmitting]);

  return (
    <>
      <SeoController
        title={translate('signup.breeder.seo.title')}
        description={translate('signup.breeder.seo.description')}
        keywords={translate('signup.breeder.seo.keywords')}
      />
      <FormikProvider value={form}>
        <JoinBreederPage
          loginLink={routeLocationDescriptor('login')}
          onSubmitClick={onSubmitClickClickHandler}
          loading={isLoading}
        />
      </FormikProvider>
    </>
  );
};
